import axios from '@/lib/axios'
import { buildAdminRequestConfig, type OrganizationId } from '@/lib/organization-scoping'
import { conditionalObjectMerge } from '@/lib/utils'
import type { QueryFunctionContext } from '@tanstack/react-query'
import type {
  ActivityFeedCounts,
  ActivityFeedFilters,
  ActivityFeedIndexResponse,
  ActivityFeedReadAllResponse,
  ActivityFeedReadState,
  ActivityFeedViewedResponse,
} from './activity-feed.types'

export interface ActivityFeedOrganizationScope {
  organizationId: OrganizationId
}

export interface ActivityFeedIndexKeyEntries {
  organizationId: number
  filters?: ActivityFeedFilters | undefined
  page?: number | undefined
  pageSize?: number | undefined
}

export interface ActivityFeedTarget extends ActivityFeedOrganizationScope {
  type: string
  sourceId: number | string
}

type ActivityFeedQueryContext = Pick<
  QueryFunctionContext<[ActivityFeedIndexKeyEntries]>,
  'queryKey'
>

async function fetchActivityFeed({
  organizationId,
  filters = {},
  page = 1,
  pageSize = 20,
}: ActivityFeedIndexKeyEntries): Promise<ActivityFeedIndexResponse> {
  const params = conditionalObjectMerge(
    {
      page,
      page_size: pageSize,
      ...(filters?.onlyUnread ? { only_unread: true } : {}),
      ...(filters?.type ? { type: filters.type } : {}),
    },
    {}
  )

  return (
    (await axios.get<ActivityFeedIndexResponse>(
      '/activity-feed',
      buildAdminRequestConfig({ organizationId, params })
    )) || {
      data: [],
      meta: {
        current_page: page,
        page_size: pageSize,
        total: 0,
        last_page: 1,
      },
      unread_count: 0,
      new_count: 0,
    }
  )
}

async function fetchActivityFeedCounts({
  organizationId,
  filters = {},
}: ActivityFeedIndexKeyEntries): Promise<ActivityFeedCounts> {
  const params = conditionalObjectMerge(
    {
      ...(filters?.type ? { type: filters.type } : {}),
    },
    {}
  )

  return (
    (await axios.get<ActivityFeedCounts>(
      '/activity-feed/counts',
      buildAdminRequestConfig({ organizationId, params })
    )) || {
      unread_count: 0,
      new_count: 0,
    }
  )
}

export async function getActivityFeed({
  queryKey: [{ organizationId, filters = {}, page = 1, pageSize = 20 }],
}: ActivityFeedQueryContext): Promise<ActivityFeedIndexResponse> {
  return fetchActivityFeed({ organizationId, filters, page, pageSize })
}

export async function getActivityFeedCounts({
  queryKey: [{ organizationId, filters = {} }],
}: ActivityFeedQueryContext): Promise<ActivityFeedCounts> {
  return fetchActivityFeedCounts({ organizationId, filters })
}

export async function getInfiniteActivityFeed({
  pageParam = 1,
  queryKey: [{ organizationId, filters = {}, pageSize = 20 }],
}: ActivityFeedQueryContext & { pageParam?: number }): Promise<ActivityFeedIndexResponse> {
  return fetchActivityFeed({
    organizationId,
    filters,
    page: pageParam,
    pageSize,
  })
}

export async function markActivityFeedViewed({ organizationId }: ActivityFeedOrganizationScope) {
  return axios.post<ActivityFeedViewedResponse>(
    '/activity-feed/view',
    null,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function markAllActivityFeedRead({ organizationId }: ActivityFeedOrganizationScope) {
  return axios.put<ActivityFeedReadAllResponse>(
    '/activity-feed/read-all',
    null,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function markActivityFeedRead({ organizationId, type, sourceId }: ActivityFeedTarget) {
  return axios.put<ActivityFeedReadState>(
    `/activity-feed/${type}/${sourceId}/read`,
    null,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function markActivityFeedUnread({
  organizationId,
  type,
  sourceId,
}: ActivityFeedTarget) {
  return axios.put<ActivityFeedReadState>(
    `/activity-feed/${type}/${sourceId}/unread`,
    null,
    buildAdminRequestConfig({ organizationId })
  )
}
